import React, { useReducer, useEffect, Suspense, lazy, useMemo, useRef } from 'react'
import { render } from 'react-dom'
import './assets/global.scss'
import './assets/css/utils/helper.scss'
import { StateContext, DispatchContext } from './AppContext'
import { reducer, initialState } from './reducers'
import { baseApiURL, baseApiSocketURL } from 'src/utils/utils'
import { BrowserRouter, Switch, Route, useLocation, useHistory } from 'react-router-dom'
import { init, ErrorBoundary } from '@sentry/react'
// import { Integrations } from '@sentry/tracing'
import axios from 'axios'
import useSocket from 'src/hooks/useSocket'
import cookie from 'cookie_js'
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// Components
import Aside from './components/aside/Aside'
import PageSpinner from 'src/components/spinners/PageSpinner'
import DiscountCode from './pages/discountCode/DiscountCode'
import Modal from './components/modal/Modal'
import { add } from 'date-fns'
import { gaLogin, gaLogout, initGA } from './utils/ga/ga'
import { gaUserParameterMapper } from './utils/ga/gaMapper'

const FrontPage = lazy(() => import('./pages/frontpage/FrontPage'))
const Orders = lazy(() => import('./pages/orders/Orders'))
const Order = lazy(() => import('./pages/orders/Order'))
const Users = lazy(() => import('./pages/users/Users'))
const Vouchers = lazy(() => import('./pages/vouchers/Vouchers'))
const Attributes = lazy(() => import('./pages/attributes/Attributes'))
const Companies = lazy(() => import('./pages/companies/Companies'))
const Addresses = lazy(() => import('./pages/addresses/Addresses'))
const Company = lazy(() => import('./pages/companies/Company'))
const Settings = lazy(() => import('./pages/settings/Settings'))
const Accounts = lazy(() => import('./pages/accounts/Accounts'))
const BookingModule = lazy(() => import('./pages/bookingModule/BookingModule'))
const Location = lazy(() => import('./pages/location/Location'))
const FixedTrips = lazy(() => import('./pages/fixedTrips/FixedTrips'))
const CustomAddresses = lazy(() => import('./pages/addresses/AddressesPOI'))

const container = document.getElementById('app')
if (process.env.NODE_ENV === 'production') {
  init({ dsn: 'https://2f75a11ae3e04f9a82da743205d73b8f@o491226.ingest.sentry.io/5556464' })
}

window.showV2Features = true

initGA()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

const Index = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const location: any = useLocation()
  const history = useHistory()
  const dateInterval = useRef(null)
  const socketData = useSocket(state.user.id ? `${baseApiSocketURL()}/users/${state.user.id}?access_token=${state.user.accessToken}` : null)

  useEffect(() => {
    if (cookie.get('access_token') && location.pathname === '/login') {
      history.push('/order')
    }
  }, [history, location])


  useEffect(() => {
    if (socketData) {
      if (socketData.activeAccessToken === false) {
        cookie.remove('access_token')
        cookie.remove('refresh_token')
        gaLogout()
        dispatch({
          type: 'SET_USER_INFO',
          user: {}
        })
        history.push('login')
      }
    }
  }, [history, socketData]);

  useEffect(() => {
    const updateDate = (date) => {
      clearTimeout(dateInterval.current)
      dispatch({ type: 'SET_DATE', date })
      const remainingSeconds = 60 - date.getSeconds()
      dateInterval.current = window.setTimeout(() => updateDate(add(date, { seconds: remainingSeconds })), Number(remainingSeconds + '000'));
    }

    axios(`${baseApiURL()}/current_date`)
      .then((result) => {
        updateDate(new Date(result.data))
      }).catch((err) => {
        toast(err.response.data.message)
      })
  }, [])

  useEffect(() => {
    setInterval(function () {
      const accessToken = cookie.get('access_token')
      if (!accessToken) {
        fetchAccessToken()
      }
    }, 300000);

    const fetchAccessToken = () => {
      const refreshToken = cookie.get('refresh_token')
      const accessToken = cookie.get('access_token')

      if (!accessToken) {
        if (refreshToken) {
          axios.post(`${baseApiURL()}/oauth/access_token`, {
            grant_type: 'refresh_token',
            refresh_token: refreshToken
          }).then(result => {
            dispatch({
              type: 'SET_USER_INFO',
              user: {
                accessToken: result.data.access_token,
                id: result.data.userId
              }
            })
            cookie.set('access_token', result.data.access_token, {
              expires: new Date(new Date().getTime() + 50 * 60 * 1000), // expires in 50min
              path: '/',
              secure: true,
              sameSite: 'strict'
            })
            cookie.set('user_id', result.data.userId, {
              expires: new Date(new Date().getTime() + 50 * 60 * 1000), // expires in 50min
              path: '/',
              secure: true,
              sameSite: 'strict'
            })
            cookie.set('refresh_token', result.data.refresh_token, {
              expires: 1, // expires in 24 hour
              path: '/',
              secure: true,
              sameSite: 'strict'
            })
          }).catch((err) => {
            history.push('/login')
            cookie.remove('refresh_token')
            gaLogout()
            toast(err.response.data.message)
          })
        } else if (location.pathname && !location.pathname.startsWith('/resetPassword') && !location.pathname.startsWith('/register') && !location.pathname.startsWith('/login')) {
          history.push('/login')
        }
      }
    }

    fetchAccessToken()
  }, [history, location.pathname])

  useEffect(() => {
    // Check if we're on a company detail route (routes with companyId param)
    // If so, skip fetching logged-in user's company to avoid overwriting param-based company data
    const isCompanyDetailRoute = location.pathname.startsWith('/companies/') && location.pathname.split('/').length > 2
    
    if (cookie.get('user_id') && cookie.get('access_token') && !state.user.companyId) {
      axios.get(`${baseApiURL()}/users/${cookie.get('user_id')} `, {
        headers: {
          Authorization:
            `Bearer ${cookie.get('access_token')}`
        }
      })
        .then((result) => {
          dispatch({
            type: 'SET_USER_INFO',
            user: {
              branchId: result.data.branchId,
              companyId: result.data.companyId,
              firstName: result.data.firstName,
              lastName: result.data.lastName,
              branchFullAddress: result.data.branchFullAddress,
              accessToken: cookie.get('access_token'),
              id: cookie.get('user_id'),
              role: result.data.role,
              availableAccounts: result.data.availableAccounts,
              phone: result.data.phone
            }
          })
          
          // Only fetch logged-in user's company if NOT on a company detail route
          // This prevents overwriting param-based company data from Company.tsx
          if (!isCompanyDetailRoute) {
            axios(`${baseApiURL()}/companies/${result.data.companyId}`, {
              headers: {
                Authorization:
                  `Bearer ${cookie.get('access_token')}`
              }
            }).then(companyResult => {
            dispatch({ type: 'SET_COMPANY', company: result.data })
              gaLogin(gaUserParameterMapper(parseInt(cookie.get('user_id')), result.data.role), false)
              
            }).catch()
          }
        }).catch((err) => {
          cookie.remove('access_token')
          cookie.remove('refresh_token')
          toast(err.response.data.message)
          dispatch({
            type: 'SET_USER_INFO',
            user: {}
          })
          gaLogout()
          history.push('/login')
        })
    }
  }, [history, state.user.accessToken, state.user.comapanyId, state.user.companyId])

  const background = useMemo(() => {
    return location.state && location.state.background
  }, [location])

  return (
    <>
    <ErrorBoundary fallback={<div>An error has occurred</div>}>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={<PageSpinner />}>
            <DispatchContext.Provider value={dispatch}>
              <StateContext.Provider value={state}>
                <Suspense fallback={<PageSpinner />}>
                  <Switch location={background || location}>
                    <Route exact path={["/", "/login"]} render={() => <FrontPage type='login' />} />
                    <Route path='/register' render={() => <FrontPage type='register' />} />
                    <Route exact path='/resetPassword' render={() => <FrontPage type='resetPassword' />} />
                    <Route exact path='/resetPassword/:resetKey' render={() => <FrontPage type='newPassword' />} />
                    <Route path='/orders' render={() => <Orders />} />
                    <Route path='/order' render={() => <Order />} />
                    <Route path='/settings' render={() => <Settings />} />
                    <Route path='/vouchers' render={() => <Vouchers />} />
                    <Route path='/attributes' render={() => <Attributes />} />
                    <Route path='/discount-Code' render={() => <DiscountCode />} />
                    <Route exact path='/companies' render={() => <Companies />} />
                    <Route exact path='/companies/:companyId' render={() => <Company />} />
                    <Route exact path='/companies/:companyId/booking-module' render={() => <BookingModule />} />
                    <Route exact path='/companies/:companyId/users' render={() => <Users />} />
                    <Route exact path='/companies/:companyId/accounts' render={() => <Accounts />} />
                    <Route exact path='/companies/:companyId/branch/:branchId' render={() => <Location />} />
                    <Route exact path='/companies/:companyId/:branchId/users' render={() => <Users />} />
                    <Route exact path='/companies/:companyId/:branchId/addresses' render={() => <Addresses />} />
                    <Route exact path='/companies/:companyId/:branchId/fixed-trips' render={() => <FixedTrips />} />
                    <Route exact path='/companies/:companyId/:branchId/custom-addresses' render={() => <CustomAddresses />} />
                  </Switch>
                  <Aside />
                  <Modal />
                  <ToastContainer toastClassName={'notifcation'} hideProgressBar={true} position='bottom-left' />
                </Suspense>
              </StateContext.Provider >
            </DispatchContext.Provider >
          </Suspense>
        </QueryClientProvider>
      </ErrorBoundary >
    </>
  )
}

render(
  <BrowserRouter>
    <Index />
  </BrowserRouter>,
  container)






